/**
 * A multi-line text input, in the same box as `Input`. Put it inside a `Field`.
 *
 * `autoGrow` lets the box grow with what is typed, up to `maxRows`, then scroll. `code` is
 * monospace and starts at 16 rows, for the rule JSON staff edit in the admin.
 */
import * as stylex from "@stylexjs/stylex";
import "@lattice/tokens/space.stylex";
import { space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import { controlStyle } from "./controlStyle.ts";
import { focusRing } from "./focus.ts";
const _temp = {
  kAzted: "x82snj4",
  "$$css": true
};
const _temp2 = {
  kskxy: "x1jols5v",
  "$$css": true
};
const styles = {
  area: {
    k1xSpc: "x1lliihq",
    k8WAf4: "x1jyo4x1",
    kHenm0: "x288g5",
    $$css: true
  },
  grow: {
    kOwNse: "x5f5z56",
    $$css: true
  },
  rows: (rows: number) => [_temp, {
    "--x-minHeight": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(`calc(${String(rows)}lh + ${space.sm} * 2)`)
  }],
  maxRows: (rows: number) => [_temp2, {
    "--x-maxHeight": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(`calc(${String(rows)}lh + ${space.sm} * 2)`)
  }],
  code: {
    kMv6JI: "x1bc1bhi",
    khDVqt: "x1sdyfia",
    kXHlph: "xw2csxc",
    $$css: true
  }
};
export interface TextareaProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly id?: string | undefined;
  readonly variant?: "plain" | "code";
  readonly rows?: number;
  readonly autoGrow?: boolean;
  readonly maxRows?: number;
  readonly name?: string;
  readonly placeholder?: string | undefined;
  readonly maxLength?: number | undefined;
  readonly required?: boolean | undefined;
  readonly "aria-required"?: true | undefined;
  readonly disabled?: boolean;
  readonly readOnly?: boolean;
  readonly autoFocus?: boolean | undefined;
  readonly "aria-describedby"?: string | undefined;
  readonly "aria-invalid"?: true | undefined;
  readonly testId?: string;
}
export function Textarea({
  value,
  onChange,
  variant = "plain",
  rows,
  autoGrow = false,
  maxRows,
  disabled = false,
  readOnly = false,
  testId,
  "aria-invalid": invalid,
  ...rest
}: TextareaProps) {
  const start = rows ?? (variant === "code" ? 16 : 3);
  return <textarea {...rest} rows={start} value={value} disabled={disabled} readOnly={readOnly} aria-invalid={invalid} spellCheck={variant === "code" ? false : undefined} onChange={event => onChange(event.currentTarget.value)} data-testid={testId} {...stylex.props(controlStyle.box, styles.area, styles.rows(start), autoGrow && styles.grow, maxRows !== undefined && styles.maxRows(maxRows), variant === "code" && styles.code, invalid === true && controlStyle.invalid, readOnly && controlStyle.readOnly, disabled && controlStyle.disabled, focusRing)} />;
}